import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

const Wrap = styled.div`
  position: relative;
  width: 100%;
`;

const Trigger = styled.button`
  width: 100%;
  padding: 9px 30px 9px 14px;
  border: 1px solid #d0d0d0;
  border-radius: 8px;
  background: #ffffff;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  position: relative;
  box-sizing: border-box;

  &::after {
    content: "▾";
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #555;
  }
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 100%;
  width: max-content;
  max-width: 360px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 30;
  padding: 8px;
  box-sizing: border-box;
`;

const Search = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d0d0d0;
  border-radius: 6px;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  box-sizing: border-box;
  outline: none;
  &:focus { border-color: #000; }
  @media (max-width: 700px) {
    font-size: 16px;
  }
`;

const Options = styled.div`
  max-height: 280px;
  overflow-y: auto;
  margin-top: 6px;
`;

const Option = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  &:hover { background: #f5f5f5; }
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #f0f0f0;
  margin-top: 6px;
  padding-top: 6px;
  font-size: 12px;
  color: #666;
`;

const LinkBtn = styled.button`
  background: none;
  border: none;
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 4px;
`;

export type MultiSelectOption = string | { value: string; label: string };

interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  allLabel: string;
  noun: string;
  single?: boolean;
}

const MultiSelect = ({ options, selected, onChange, allLabel, noun, single = false }: MultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const normalized = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const labelOf = (value: string) => normalized.find((o) => o.value === value)?.label ?? value;
  const visible = normalized.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()));
  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((s) => s !== value) : [...selected, value]);
  const choose = (value: string) => {
    onChange([value]);
    setOpen(false);
    setQuery("");
  };

  const label =
    selected.length === 0
      ? allLabel
      : selected.length === 1
      ? labelOf(selected[0])
      : `${selected.length} ${noun} selected`;

  return (
    <Wrap ref={ref}>
      <Trigger type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} title={selected.map(labelOf).join(", ")}>
        {label}
      </Trigger>
      {open && (
        <Panel>
          <Search
            autoFocus
            placeholder={`Search ${noun}…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Options>
            {visible.length === 0 ? (
              <div style={{ padding: 8, fontSize: 13, color: "#888" }}>No matches.</div>
            ) : (
              visible.map((o) =>
                single ? (
                  <Option
                    key={o.value}
                    onClick={() => choose(o.value)}
                    style={selected.includes(o.value) ? { fontWeight: 600, background: "#f5f5f5" } : undefined}
                  >
                    {o.label}
                  </Option>
                ) : (
                  <Option key={o.value}>
                    <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
                    {o.label}
                  </Option>
                )
              )
            )}
          </Options>
          {!single && (
          <Footer>
            <span>{selected.length} selected</span>
            <span>
              {query && visible.length > 0 && (
                <LinkBtn type="button" onClick={() => onChange(Array.from(new Set([...selected, ...visible.map((o) => o.value)])))}>
                  Select matches
                </LinkBtn>
              )}
              <LinkBtn type="button" onClick={() => onChange([])} disabled={selected.length === 0}>
                Clear
              </LinkBtn>
            </span>
          </Footer>
          )}
        </Panel>
      )}
    </Wrap>
  );
};

export default MultiSelect;
